import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import ElementSettingsContext from '../helpers/element-settings-context';
import mappingElementIdsToAttributes from '../helpers/mapping-element-ids-to-attributes';
import getBatchValues from '../helpers/get-batch-values';
import createBatch from '../helpers/create-batch';
import modifyBatchChanges from '../helpers/modify-batch-changes';

const { ToggleSection, Stack, Box } = designSystem;

function ToggleableSectionControl({
  id,
  elementIds,
  attributes,
  title,
  popover = null,
  computeFallbackValue = null,
  onChange,
  children,
}) {
  const { state } = useContext(ElementSettingsContext);

  const elementIdsToAttributes = mappingElementIdsToAttributes(elementIds, attributes);
  const flattenedValues = getBatchValues(state, { elementIds, attributes }).flat();
  const areAllValuesBooleans = flattenedValues.every((value) => typeof value === 'boolean');
  const isToggleEnabled = areAllValuesBooleans
    ? flattenedValues.every((value) => value !== false)
    : flattenedValues.some((value) => value !== false);

  const handleChange = useCallback(
    (value) => {
      const batch = modifyBatchChanges({
        batch: createBatch({
          elementIds,
          attributes,
          value,
        }),
        onFilterCallback: ({ elementId, attribute }) =>
          elementIdsToAttributes[elementId].includes(attribute),
        onComputeValueCallback: ({ elementId, attribute, value: val }) =>
          val && computeFallbackValue ? computeFallbackValue({ elementId, attribute }) : val,
      });

      onChange({ batch, controlId: id, value });
    },
    [id, elementIds, attributes, onChange, computeFallbackValue, elementIdsToAttributes]
  );

  return (
    <Box
      paddingTop="spacing-xs"
      paddingBottom={isToggleEnabled ? 'spacing-s' : 'spacing-xs'}
      paddingLeft="spacing-m"
      paddingRight="spacing-m"
    >
      <ToggleSection
        value={isToggleEnabled}
        title={title}
        {...(popover && { popoverProps: popover })}
        onChange={handleChange}
      >
        <Stack gap="spacing-2-xs">{children}</Stack>
      </ToggleSection>
    </Box>
  );
}

ToggleableSectionControl.propTypes = {
  id: PropTypes.string.isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  attributes: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  popover: PropTypes.shape({
    title: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
  }),
  computeFallbackValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default ToggleableSectionControl;
